<template>
  <div class="welcome">
    <div class="bg-grid"></div>
    <div class="bg-glow glow-1"></div>
    <div class="bg-glow glow-2"></div>
    <div class="bg-glow glow-3"></div>

    <header class="nav">
      <div class="brand">
        <span class="brand-mark">▣</span>
        <span class="brand-name">CubeSolv</span>
      </div>
      <nav class="nav-links">
        <a href="#features">Features</a>
        <a href="#about">About</a>
        <a href="#contact">Contact</a>
      </nav>
    </header>

    <main class="hero">
      <div class="badge">
        <span class="dot"></span>
        Just released · v0.1
      </div>

      <h1 class="title">
        Solve any cube,
        <span class="gradient-text">instantly.</span>
      </h1>

      <p class="subtitle">
        Upload your cube state and let our algorithm find the shortest solution in milliseconds.<br />
        Step-by-step guidance, 3D visualization, and multiple solving strategies — all built in.
      </p>

      <div class="actions">
        <button class="btn btn-primary" @click="onStart">
          Get Started
          <span class="arrow">→</span>
        </button>
        <button class="btn btn-ghost" @click="onLearn">Learn More</button>
      </div>

      <div class="stats">
        <div class="stat">
          <div class="stat-num">&lt;20</div>
          <div class="stat-label">Avg. Moves</div>
        </div>
        <div class="divider"></div>
        <div class="stat">
          <div class="stat-num">12ms</div>
          <div class="stat-label">Solve Time</div>
        </div>
        <div class="divider"></div>
        <div class="stat">
          <div class="stat-num">3D</div>
          <div class="stat-label">Visualization</div>
        </div>
      </div>
    </main>

    <footer class="foot">
      <span>© {{ year }} CubeSolv</span>
      <span class="sep">·</span>
      <span>Made with Vue 3</span>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'WelcomeView',
  data() {
    return {
      year: new Date().getFullYear(),
    };
  },
  methods: {
    onStart() {
      this.$emit('start');
    },
    onLearn() {
      window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
    },
  },
};
</script>

<style scoped>
.welcome {
  position: relative;
  min-height: 100vh;
  width: 100%;
  overflow: hidden;
  color: #e9ecf2;
  font-family: 'Inter', 'Helvetica Neue', -apple-system, 'PingFang SC',
    'Microsoft YaHei', sans-serif;
  background:
    radial-gradient(ellipse at 20% 0%, #1a1f3a 0%, transparent 55%),
    radial-gradient(ellipse at 100% 100%, #2a1a3a 0%, transparent 50%),
    linear-gradient(180deg, #0a0c18 0%, #0d0f1f 100%);
  display: flex;
  flex-direction: column;
}

.bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 56px 56px;
  mask-image: radial-gradient(ellipse 80% 60% at 50% 40%, #000 30%, transparent 75%);
  -webkit-mask-image: radial-gradient(ellipse 80% 60% at 50% 40%, #000 30%, transparent 75%);
  pointer-events: none;
}

.bg-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(120px);
  pointer-events: none;
  animation: float 14s ease-in-out infinite;
}
.glow-1 {
  width: 520px;
  height: 520px;
  background: #5b6cff;
  top: -120px;
  left: -120px;
  opacity: 0.55;
}
.glow-2 {
  width: 520px;
  height: 520px;
  background: #c43cff;
  bottom: -160px;
  right: -120px;
  opacity: 0.4;
  animation-delay: -4s;
}
.glow-3 {
  width: 360px;
  height: 360px;
  background: #ff6ec7;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.18;
  animation-delay: -8s;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0); }
  50%      { transform: translate(20px, -30px); }
}
.glow-3 {
  animation-name: float-center;
}
@keyframes float-center {
  0%, 100% { transform: translate(-50%, -50%); }
  50%      { transform: translate(calc(-50% + 30px), calc(-50% - 20px)); }
}

.nav {
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 22px 64px;
}
.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
}
.brand-mark {
  font-size: 22px;
  color: #7b8cff;
  text-shadow: 0 0 18px rgba(123, 140, 255, 0.6);
}
.brand-name {
  font-size: 18px;
  background: linear-gradient(90deg, #fff, #b9c0ff);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.nav-links {
  display: flex;
  gap: 28px;
}
.nav-links a {
  color: rgba(233, 236, 242, 0.7);
  text-decoration: none;
  font-size: 14px;
  transition: color 0.2s;
}
.nav-links a:hover {
  color: #fff;
}

.hero {
  position: relative;
  z-index: 2;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 24px 80px;
  max-width: 880px;
  margin: 0 auto;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border: 1px solid rgba(123, 140, 255, 0.35);
  background: rgba(123, 140, 255, 0.08);
  border-radius: 999px;
  font-size: 12.5px;
  color: #c8d0ff;
  margin-bottom: 32px;
  backdrop-filter: blur(8px);
}
.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #6cf0a8;
  box-shadow: 0 0 10px #6cf0a8;
  animation: pulse 2s ease-in-out infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.4; }
}

.title {
  font-size: clamp(48px, 7vw, 96px);
  line-height: 1.05;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 28px;
}
.gradient-text {
  display: inline-block;
  background: linear-gradient(120deg, #7b8cff 0%, #c43cff 50%, #ff6ec7 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.subtitle {
  font-size: 17px;
  line-height: 1.75;
  color: rgba(233, 236, 242, 0.7);
  max-width: 600px;
  margin: 0 0 40px;
}

.actions {
  display: flex;
  gap: 14px;
  margin-bottom: 64px;
}
.btn {
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  padding: 14px 28px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.18s, box-shadow 0.18s, background 0.18s;
}
.btn-primary {
  background: linear-gradient(120deg, #6f7dff 0%, #a45bff 100%);
  color: #fff;
  box-shadow:
    0 8px 24px rgba(111, 125, 255, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);
}
.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow:
    0 12px 30px rgba(111, 125, 255, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);
}
.btn-primary .arrow {
  transition: transform 0.2s;
}
.btn-primary:hover .arrow {
  transform: translateX(4px);
}
.btn-ghost {
  background: transparent;
  color: #e9ecf2;
  border: 1px solid rgba(255, 255, 255, 0.18);
}
.btn-ghost:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.32);
}

.stats {
  display: flex;
  align-items: center;
  gap: 32px;
  padding: 20px 32px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(8px);
}
.stat-num {
  font-size: 28px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.02em;
}
.stat-label {
  font-size: 12.5px;
  color: rgba(233, 236, 242, 0.55);
  margin-top: 2px;
}
.divider {
  width: 1px;
  height: 36px;
  background: rgba(255, 255, 255, 0.12);
}

.foot {
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 16px;
  font-size: 12.5px;
  color: rgba(233, 236, 242, 0.4);
}
.sep {
  margin: 0 8px;
}

@media (max-width: 720px) {
  .nav { padding: 18px 24px; }
  .nav-links { display: none; }
  .hero { padding: 24px 20px 60px; }
  .actions { flex-direction: column; width: 100%; max-width: 280px; }
  .btn { justify-content: center; width: 100%; }
  .stats { gap: 16px; padding: 16px 20px; }
  .stat-num { font-size: 22px; }
}
</style>
